import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {isVatActive, isVatUndefined} from './vat';
import {ERROR_CODES} from '@/constants';

dayjs.extend(utc);

export const cartDataSource = {
  LOCALE: 'locale',
  PRICING: 'pricing',
  PRICING_LOCALE: 'pricingLocale',
};

export const cartType = {
  ESRISTORE: 'esristore',
  BUILDANDSHARE: 'buildandshare',
};

export const priceType = {
  PRICE: 'price',
  QUANTITY_ONE_PRICE: 'quantityOnePrice',
};

const privatePriceType = {
  CORE_PRICE: 'corePrice',
};

const PERSONAL_USE_SKUS = ['165619'];
const STUDENT_USE_SKUS = ['165620'];
const K12_SKUS = ['174329', '165576'];
const HIGHER_ED_SKUS = ['157570', '157571', '157572', '157574'];

/** @return {PricingTier} */
export function findPricingTierByQty(pricingTiers, qty) {
  if (!pricingTiers?.length) return null;

  return pricingTiers.find((pricingTier) => {
    if (pricingTier._id === 'mockPricingTier') return true;

    return qty >= pricingTier.qtyFrom && qty <= pricingTier.qtyTo;
  });
}

/** @return {number} */
export function getItemQty(item) {
  let totalQty = item.quantity;
  if (!item?.metadata?.subscriptions?.selected?.length) return totalQty;

  // For certain products discount eligbility needs to include current entitled quantity.
  for (const selectedSubscription of item.metadata.subscriptions.selected) {
    if (!selectedSubscription.usersEligibility?.[item.materialNumber]) continue;

    totalQty += selectedSubscription.usersEligibility[item.materialNumber].currentEntitled;
  }

  // In some cases, the total quantity is current quantity selected
  // plus the quantity that already exists for that customer for that product.
  return totalQty;
}

/** @return {boolean} */
export function hasSubscriptionSelected(item) {
  return !!item.metadata?.subscriptions?.selected?.length;
}

export function isCoreLicenseSelector(item) {
  return item?.metadata?.subscriptions?.selectorType === 'coreLicenseSelector';
}

export function hasVolumeDiscount(item) {
  const pct = item?.metadata?.pricing?.volumeDiscountPercentage;
  return pct != null && pct > 0;
}

/** @return {boolean} */
export function hasInvalidItemPrice(cart) {
  if (!cart?.lineItems?.length || isVatUndefined()) return false;

  // Check if any of the cart item vat prices are invalid based on the current vat selection.
  return cart.lineItems.some((item) => {
    const prices = getItemPrices(item);
    const validPrice = prices.price;

    return !!validPrice && validPrice !== item.price;
  });
}

/**
 * Resolves the price type based on the item and the provided type.
 * @param {Object} item - The item.
 * @param {string} _type - The price type.
 * @return {string} */
export function resolvePriceType(item, type) {
  if (type === priceType.PRICE && isCoreLicenseSelector(item) && hasSubscriptionSelected(item)) {
    // Core license with active subscription, use core price
    type = privatePriceType.CORE_PRICE;
  } else if (type === priceType.PRICE && !hasSubscriptionSelected(item)) {
    // No active subscription, use single quantity price to exclude volume discounts
    type = priceType.QUANTITY_ONE_PRICE;
  }

  return type;
}

/**
 * Calculates the item price based on the provided type.
 * @param {Object} item - The item.
 * @param {string} type - The price type.
 * @return {number} */
export function calculateItemPrice(item, type) {
  const vatActive = isVatActive();
  const resolvedType = resolvePriceType(item, type);
  switch (resolvedType) {
    case priceType.PRICE:
    case priceType.QUANTITY_ONE_PRICE: {
      // Calculate price based on active subscription and assigned quantity, including pro-rated pricing and volume discounts.
      // If type is QUANTITY_ONE_PRICE, calculate price for a single quantity to exclude volume discounts.
      const max = item.metadata?.maxQtyAllowed || Number.MAX_SAFE_INTEGER;
      const quantity = type === priceType.QUANTITY_ONE_PRICE ? 1 : Math.min(getItemQty(item), max);
      const pricingTier = findPricingTierByQty(item?.metadata?.pricing?.pricingTiers, quantity);
      const price = vatActive ? pricingTier?.vatAmount : pricingTier?.amount;
      return price;
    }
    case privatePriceType.CORE_PRICE: {
      // Calculate the total price based on the quantities and prices of the selected subscriptions
      const price = item.metadata.subscriptions.selected.reduce((acc, subscription) => {
        if (subscription?.dates?.[0]?.pricingTiers) {
          const assignedQty = subscription.assignedQty;
          const pricingTier = findPricingTierByQty(subscription.dates[0].pricingTiers, assignedQty);
          const _price = vatActive ? pricingTier?.vatAmount : pricingTier?.amount;

          return acc + _price * assignedQty;
        }

        return acc;
      }, 0);
      return price / getItemQty(item);
    }
    default:
      return null;
  }
}

/**
 * Gets the item prices based on the current vat selection.
 * @param {Object} item - The item.
 * @return {prices} */
export function getItemPrices(item) {
  const price = (!isVatUndefined() && calculateItemPrice(item, priceType.PRICE)) || item.price;
  const quantityOnePrice = (!isVatUndefined() && calculateItemPrice(item, priceType.QUANTITY_ONE_PRICE)) || item.metadata?.pricing?.quantityOnePrice;

  return {
    price,
    quantityOnePrice,
  };
}

/** @return {void} */
export function recalculateCartPrices(cart) {
  if (!cart?.lineItems?.length || isVatUndefined()) return;

  for (const item of cart.lineItems) {
    const prices = getItemPrices(item);
    item.price = prices.price;
    if (item.metadata?.pricing) {
      item.metadata.pricing.quantityOnePrice = prices.quantityOnePrice;
    }
  }
}

/**
 * Converts cart data to a cart model.
 * @param {Object} cartData - The cart data.
 * @param {string} [sourceType=cartDataSource.PRICING] - The source type of the cart data.
 * @returns {Object} The cart model.
 */
export function toCartModel(cartData, sourceType = cartDataSource.PRICING) {
  let model;
  const template = {
    channel: cartData.channel || 'eCommerce',
    cartType: cartData.cartType || cartType.ESRISTORE,
    lineItems: cartData.lineItems || [],
    status: cartData.status || 'new',
  };

  if (sourceType === cartDataSource.LOCALE) {
    model = {
      ...template,
      locale: cartData.locale.supportedCurrencies[0].storeLocale,
      zone: cartData.locale.zone,
      currency: cartData.locale.supportedCurrencies[0].currencyCode,
      distributorNumber: cartData.locale.distributorNumber,
      metadata: {
        isOmni: cartData.locale.isOmni,
        storeDomain: cartData.locale.storeDomain,
        formattedLocale: cartData.locale.supportedLocales[0].formattedLocale,
        currency: cartData.locale.supportedCurrencies[0],
        ...cartData.locale.cartMetadata,
      },
      ...(cartData.username && {username: cartData.username}),
    };
  } else if (sourceType === cartDataSource.PRICING) {
    model = {
      ...template,
      locale: cartData.locale,
      zone: cartData.zone,
      currency: cartData.currency,
      distributorNumber: Number.parseInt(cartData.distributorNumber, 10),
      metadata: cartData.sessionMetadataUpdate || cartData.metadata,
      ...(cartData.customerNumber && {customerNumber: cartData.customerNumber}),
      ...(cartData.createDate && {createDate: cartData.createDate}),
    };
  } else if (sourceType === cartDataSource.PRICING_LOCALE) {
    model = {
      ...template,
      locale: cartData.locale,
      zone: cartData.zone,
      currency: cartData.currency,
      distributorNumber: Number.parseInt(cartData.distributorNumber, 10),
      metadata: {
        isOmni: cartData.isOmni,
        storeDomain: cartData.storeDomain,
        formattedLocale: cartData.formattedLocale,
        ...cartData.cartMetadata,
      },
    };
  }

  return model;
}

function hasMaterialNumberIn(item, skus) {
  const materialNumber = item?.materialNumber?.toString();
  return !!materialNumber && skus.includes(materialNumber);
}

/**
 * Check if the item is a personal use product.
 * @param {Object} item - The item.
 * @return {boolean}
 */
export function isPersonalUse(item) {
  return hasMaterialNumberIn(item, PERSONAL_USE_SKUS);
}

/**
 * Check if the item is a student use product.
 * @param {Object} item - The item.
 * @return {boolean}
 */
export function isStudentUse(item) {
  return hasMaterialNumberIn(item, STUDENT_USE_SKUS);
}

/**
 * Check if the item is a K12 product.
 * @param {Object} item - The item.
 * @return {boolean}
 */
export function isK12(item) {
  return hasMaterialNumberIn(item, K12_SKUS);
}

/**
 * Check if the item is a Higher Ed product.
 * @param {Object} item - The item.
 * @return {boolean}
 */
export function isHigherEd(item) {
  return hasMaterialNumberIn(item, HIGHER_ED_SKUS);
}

/**
 * Check if the item is personal or student use.
 * @param {Object} item - The item.
 * @return {boolean} True if the item is for personal or student use, false otherwise.
 */
export function isIndividualUse(item) {
  return isPersonalUse(item) || isStudentUse(item);
}

/**
 * Check if the item requires eligibility verification (student, K12, or Higher Ed).
 * @param {Object} item - The item.
 * @return {boolean}
 */
export function requiresEligibilityVerification(item) {
  return isStudentUse(item) || isK12(item) || isHigherEd(item);
}

/**
 * Merges new line items into existing cart items.
 * - If an item already exists (by SKU), its quantity is incremented
 * - If an item is new, it's added to the cart
 * @param {Array} existingLineItems - Current cart line items
 * @param {Array} newLineItems - Line items to add or merge
 * @return {Array} Updated line items array
 */
export function mergeLineItems(existingLineItems, newLineItems) {
  // Start with a copy of existing items, or an empty array
  const updatedLineItems = existingLineItems?.length ? [...existingLineItems] : [];

  // Guard against invalid input
  if (!newLineItems?.length) return updatedLineItems;

  for (const newItem of newLineItems) {
    if (!newItem) continue;

    const existingItem = findLineItemBySku(updatedLineItems, newItem.materialNumber);

    if (existingItem) {
      // Item already in cart - increment quantity
      existingItem.quantity += newItem.quantity;
    } else {
      // New item - add to cart
      updatedLineItems.push(newItem);
    }
  }

  return updatedLineItems;
}

/**
 * Finds a line item in the cart by its SKU.
 * @param {Array} lineItems
 * @param {string|number} sku
 * @returns {Object|null} The found line item or null if not found
 */
export function findLineItemBySku(lineItems, sku) {
  if (!lineItems?.length || !sku) return null;

  const match = lineItems.find((item) => item.materialNumber === Number.parseInt(sku, 10));
  return match || null;
}

/**
 * Retrieves the subscription end date, prioritizing primeEndDate over endDate.
 * @param {Object} subscription - The subscription object containing dates array.
 * @returns {string|null} The end date string (primeEndDate or endDate), or null if not found.
 */
export function getSubscriptionEndDate(subscription) {
  if (!subscription?.dates?.length) return null;

  return subscription.dates[0].primeEndDate ?? subscription.dates[0].endDate ?? null;
}

/**
 * Checks whether the cart has validation errors.
 * @param {Object} cart - The cart object.
 * @returns {boolean} True if the cart is invalid or requires subscription selection.
 */
export function hasCartValidationErrors(cart) {
  return !cart.metadata.validateCart.isCartValid
    || cart.metadata.validateCart.subscriptionSelectionRequired;
}

/**
 * Syncs request itemIds to response itemIds.
 * @param {Array} pricingResponse - Pricing response array.
 * @param {Array} pricingLineItemRequest - Pricing request array.
 */
// NOTE: Sometimes response is cached and can return previous request itemId.
export function syncPricingResponseItemIds(pricingResponse, pricingLineItemRequest) {
  for (let i = 0; i < pricingLineItemRequest.length; i++) {
    pricingResponse[i].itemId = pricingLineItemRequest[i].itemId;
  }
}

/**
 * Updates subscription pricing fields from a pricing response.
 * @param {Object} subscription - Subscription item.
 * @param {Object} pricing - Pricing data.
 */
export function updateSubscriptionPricing(subscription, pricing) {
  subscription.dates[0].endDate = dayjs.utc(pricing.endDate).format('YYYY-MM-DD');
  subscription.dates[0].price = pricing.price;
  subscription.dates[0].listPrice = pricing.listPrice;
  subscription.dates[0].vatListPrice = pricing.vatListPrice;
  subscription.dates[0].pricingTiers = pricing.pricingTiers;
  subscription.dates[0].quantityOnePrice = pricing.quantityOnePrice;
}

/**
 * Calculates the total price for a line item from its selected subscriptions.
 * @param {Object} cartLineItem - Cart line item.
 * @returns {number} Total price.
 */
export function calculateLineItemTotal(cartLineItem) {
  let total = 0;
  for (const selectedSub of cartLineItem.metadata.subscriptions.selected) {
    total += selectedSub.dates[0].price * selectedSub.assignedQty;
  }
  return total;
}

/**
 * Generates a unique subscription item ID from its component parts.
 * @param {Object} params
 * @param {string} params.subscriptionId
 * @param {string} params.sku
 * @param {string} params.endDate
 * @param {string} params.selectType
 * @returns {string}
 */
export function generateSubscriptionItemId(params) {
  return `${params.subscriptionId}${params.sku}${dayjs(params.endDate).format('YYYYMMDD')}${params.selectType}`;
}

/**
 * Updates a subscription item's subscriptionId and generates a new itemId.
 * @param {Object} subscriptionItem - Subscription item to update.
 * @param {string} newSubscriptionId - New subscription ID.
 */
export function updateSubscriptionItemId(subscriptionItem, newSubscriptionId) {
  subscriptionItem.subscriptionId = newSubscriptionId;
  subscriptionItem.itemId = generateSubscriptionItemId({
    subscriptionId: newSubscriptionId,
    sku: subscriptionItem.sku,
    endDate: getSubscriptionEndDate(subscriptionItem),
    selectType: subscriptionItem.selectType,
  });
}

/**
 * Rounds a number to two decimal places.
 * @param {number} num - Number to round.
 * @returns {number} Rounded number.
 */
export function roundToTwo(num) {
  return +(Math.round(Number(num + 'e+2')) + 'e-2');
}

/**
 * Updates an available subscription with a new subscription ID and pricing.
 * @param {Object} subscription - Available subscription item.
 * @param {Object} pricing - Pricing data.
 * @param {string} newSubscriptionId - New subscription ID.
 */
export function updateAvailableSubscription(subscription, pricing, newSubscriptionId) {
  subscription.subscriptionId = newSubscriptionId;
  updateSubscriptionPricing(subscription, pricing);
  updateSubscriptionItemId(subscription, newSubscriptionId);
}

/**
 * Updates a selected subscription with pricing and recalculates the line item total price.
 * @param {Object} subscription - Selected subscription item.
 * @param {Object} pricing - Pricing data.
 * @param {Object} cartLineItem - Cart line item.
 */
export function updateSelectedSubscription(subscription, pricing, cartLineItem) {
  subscription.dates[0].primeEndDate = dayjs.utc(pricing.endDate).format('YYYY-MM-DD');
  updateSubscriptionPricing(subscription, pricing);
  updateSubscriptionItemId(subscription, subscription.subscriptionId);

  const newLineItemTotal = calculateLineItemTotal(cartLineItem);
  cartLineItem.metadata.pricing.itemTotalPrice = newLineItemTotal;
  cartLineItem.price = roundToTwo(newLineItemTotal / cartLineItem.quantity);
}

/**
 * Determines whether a line item has auto-renewal forced/locked via its product group.
 * @param {Object} item - The cart line item.
 * @returns {boolean}
 */
export function isAutoRenewForced(item) {
  return item?.metadata?.productInfo?.productGroups?.includes('autoRenewalOn') ?? false;
}

/**
 * Determines whether a line item should have its auto-renewal flag defaulted to true.
 * This applies when the item has auto-renewal enabled and is a monthly subscription.
 * @param {Object} item - The cart line item.
 * @returns {boolean}
 */
export function shouldDefaultIsAuto(item) {
  return isAutoRenewForced(item);
}

/**
 * Determines whether the cart requires a full server-side reprocess.
 * This is true when exactly one item remains with an ArcGIS school bundle limit error at quantity 1.
 * @param {Object} cart - The cart object.
 * @returns {boolean}
 */
export function isReprocessRequired(cart) {
  if (!cart?.lineItems?.length) return false;

  const schoolBundleLimitItems = cart.lineItems.filter(
    (item) => item?.metadata?.errorCode === ERROR_CODES.ARCGIS_SCHOOL_BUNDLE_LIMIT,
  );
  return schoolBundleLimitItems.length === 1 && schoolBundleLimitItems[0].quantity === 1;
}
